// ─── L'importazione dei soci da file: la rubrica Google e Matchpoint ─────────
//
// 🗣️ Voce 26 (25/09/2026, dal mockup approvato): *«l'importazione la farò sia
// da una rubrica Google, quindi sarà un CSV, sia dall'esportazione da match
// point di tutti i contatti che ho lì dentro. Logicamente bisognerà fare un
// match per i doppioni»* · *«questa operazione verrà fatta solamente una
// volta»*.
//
// ⚖️ **Snella di proposito, perché serve una volta**: riconosce i due formati
// che lui ha, e nessun altro. Niente mappatura delle colonne a mano.
//
// ⭐ **Tutto qui è puro**, e sta in `@pmo/shared` per due ragioni: il server lo
// usa per l'anteprima e per la scrittura, e l'anteprima navigabile lo usa nel
// browser. La stessa regola in tutti e due, così quello che si guarda è quello
// che si scrive. Chi chiama passa le due cose che il puro non ha: come si
// decomprime (`inflate`) e come si leggono i byte come testo (`decodifica`).
//
// 🚨 **Le regole che non si negoziano:**
// · la stessa persona si riconosce dal TELEFONO normalizzato, poi dall'EMAIL,
//   mai dal nome: due «Marco Rossi» sono due persone;
// · il NOME di chi è già nell'anagrafica non si tocca mai;
// · di chi c'è già si completano solo le caselle VUOTE («resta la scheda»,
//   scelto da lui nel mockup): un foglio vecchio non cancella un'email
//   corretta a mano ieri, e il telefono con cui il bot riconosce un socio non
//   cambia;
// · da Matchpoint entrano nome, cognome, telefono, email e sesso: NESSUN
//   codice di Matchpoint (⇒ CLAUDE.md, «tutto ciò che fa riferimento a
//   Matchpoint deve scomparire»).
import { z } from 'zod';
import { normalizzaTelefono } from './soci.js';
// ── Il contratto ─────────────────────────────────────────────────────────────
export const FONTI_IMPORT = ['M', 'G'];
/** Il tetto di un file, in byte: l'export di Matchpoint di oggi pesa 110 kB. */
export const MASSIMO_FILE_IMPORT = 8 * 1024 * 1024;
export const domandaImportFileSchema = z.object({
    file: z
        .array(z.object({
        nome: z.string().trim().min(1).max(200),
        /** Il contenuto del file, in base64. */
        contenuto: z.string().min(1),
    }))
        .min(1)
        .max(2),
    /** `false` (il difetto) legge e basta. `true` scrive, e solo le righe `scelte`. */
    apply: z.boolean().default(false),
    scelte: z.array(z.string()).default([]),
});
export const esitoRigaImportSchema = z.enum(['nuovo', 'aggiorna', 'uguale', 'scartato']);
export const rigaImportSchema = z.object({
    /** Con cosa la schermata sceglie la riga: il telefono, o l'email, o la riga del file. */
    chiave: z.string(),
    da: z.array(z.enum(FONTI_IMPORT)),
    /** Da dove arriva, per chi la vuole ritrovare nel file: «M 12 · G 340». */
    righe: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    gender: z.enum(['M', 'F']).nullable(),
    esito: esitoRigaImportSchema,
    memberId: z.uuid().nullable(),
    perché: z.string().nullable(),
    /** Per `aggiorna`: le caselle vuote della scheda che il file riempie. */
    aggiunge: z.object({
        email: z.string().optional(),
        phone: z.string().optional(),
        gender: z.enum(['M', 'F']).optional(),
    }),
});
export const esitoImportFileSchema = z.object({
    applicato: z.boolean(),
    file: z.array(z.object({ nome: z.string(), fonte: z.enum(FONTI_IMPORT), righe: z.number().int() })),
    righe: z.array(rigaImportSchema),
    creati: z.number().int(),
    aggiornati: z.number().int(),
});
/** Si spunta da sola una riga nuova che arriva da Matchpoint: chi sta solo in rubrica si sceglie a mano. */
export const siSpuntaDaSola = (r) => r.esito === 'nuovo' && r.da.includes('M');
export class FileIllegibile extends Error {
}
/** Un foglio, righe per colonne, tutto testo. La prima riga è l'intestazione. */
export async function leggiFoglio(dati, a) {
    // Un xlsx è uno zip, e uno zip comincia con «PK».
    if (dati[0] === 0x50 && dati[1] === 0x4b)
        return leggiXlsx(dati, a);
    let testo = a.decodifica(dati);
    if (testo.charCodeAt(0) === 0xfeff)
        testo = testo.slice(1);
    return leggiCsv(testo);
}
/** CSV con la virgola, il punto e virgola o la tabulazione, e le virgolette come vuole la regola. */
export function leggiCsv(testo) {
    const aCapo = testo.search(/\r?\n/);
    const prima = aCapo < 0 ? testo : testo.slice(0, aCapo);
    const conta = (c) => prima.split(c).length - 1;
    const sep = [',', ';', '\t'].reduce((m, c) => (conta(c) > conta(m) ? c : m), ',');
    const righe = [];
    let riga = [];
    let cella = '';
    let tra = false;
    for (let i = 0; i < testo.length; i++) {
        const c = testo[i];
        if (tra) {
            if (c === '"') {
                if (testo[i + 1] === '"') {
                    cella += '"';
                    i++;
                }
                else
                    tra = false;
            }
            else
                cella += c;
        }
        else if (c === '"')
            tra = true;
        else if (c === sep) {
            riga.push(cella);
            cella = '';
        }
        else if (c === '\n' || c === '\r') {
            if (c === '\r' && testo[i + 1] === '\n')
                i++;
            riga.push(cella);
            righe.push(riga);
            riga = [];
            cella = '';
        }
        else
            cella += c;
    }
    if (cella !== '' || riga.length > 0) {
        riga.push(cella);
        righe.push(riga);
    }
    return righe;
}
const u16 = (d, i) => d[i] | (d[i + 1] << 8);
const u32 = (d, i) => (d[i] | (d[i + 1] << 8) | (d[i + 2] << 16) | (d[i + 3] << 24)) >>> 0;
/** I file di uno zip, per nome: si legge l'indice in fondo, come fa chiunque apra uno zip. */
async function apriZip(d, a) {
    let fine = -1;
    for (let i = d.length - 22; i >= Math.max(0, d.length - 65_557); i--) {
        if (u32(d, i) === 0x06054b50) {
            fine = i;
            break;
        }
    }
    if (fine < 0)
        throw new FileIllegibile('Il file Excel è rovinato: non si trova il suo indice.');
    const quanti = u16(d, fine + 10);
    let p = u32(d, fine + 16);
    const file = new Map();
    for (let n = 0; n < quanti; n++) {
        if (u32(d, p) !== 0x02014b50)
            throw new FileIllegibile('Il file Excel è rovinato.');
        const metodo = u16(d, p + 10);
        const compresso = u32(d, p + 20);
        const lNome = u16(d, p + 28);
        const lExtra = u16(d, p + 30);
        const lNota = u16(d, p + 32);
        const locale = u32(d, p + 42);
        const nome = a.decodifica(d.subarray(p + 46, p + 46 + lNome));
        const inizio = locale + 30 + u16(d, locale + 26) + u16(d, locale + 28);
        const pezzo = d.subarray(inizio, inizio + compresso);
        file.set(nome, () => (metodo === 0 ? Promise.resolve(pezzo) : a.inflate(pezzo)));
        p += 46 + lNome + lExtra + lNota;
    }
    return file;
}
const ENTITA = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" };
const testoXml = (s) => s.replace(/&(#x[0-9a-f]+|#[0-9]+|[a-z]+);/gi, (tutto, e) => e[0] === '#'
    ? String.fromCodePoint(e[1] === 'x' || e[1] === 'X' ? parseInt(e.slice(2), 16) : Number(e.slice(1)))
    : (ENTITA[e] ?? tutto));
/** Il testo di tutti i `<t>` dentro un pezzo: una cella «ricca» ne ha più d'uno. */
const iT = (xml) => [...xml.matchAll(/<t\b[^>]*>([\s\S]*?)<\/t>/g)].map((m) => testoXml(m[1])).join('');
async function leggiXlsx(d, a) {
    const zip = await apriZip(d, a);
    const leggi = async (nome) => {
        const f = zip.get(nome);
        return f ? a.decodifica(await f()) : null;
    };
    // Il PRIMO foglio, com'è scritto nella cartella di lavoro.
    let percorso = 'xl/worksheets/sheet1.xml';
    const libro = await leggi('xl/workbook.xml');
    const legami = await leggi('xl/_rels/workbook.xml.rels');
    const rid = libro?.match(/<sheet\b[^>]*\br:id="([^"]+)"/)?.[1];
    const bersaglio = rid && legami?.match(new RegExp(`<Relationship\\b[^>]*Id="${rid}"[^>]*Target="([^"]+)"`))?.[1];
    const bersaglio2 = rid && legami?.match(new RegExp(`<Relationship\\b[^>]*Target="([^"]+)"[^>]*Id="${rid}"`))?.[1];
    const t = bersaglio ?? bersaglio2;
    if (t)
        percorso = t.startsWith('/') ? t.slice(1) : `xl/${t.replace(/^\.\//, '')}`;
    const foglio = await leggi(percorso);
    if (!foglio)
        throw new FileIllegibile('Nel file Excel non c’è nessun foglio.');
    const condivise = [
        ...((await leggi('xl/sharedStrings.xml')) ?? '').matchAll(/<si\b[^>]*>([\s\S]*?)<\/si>/g),
    ].map((m) => iT(m[1]));
    const righe = [];
    for (const r of foglio.matchAll(/<row\b([^>]*?)(?:\/>|>([\s\S]*?)<\/row>)/g)) {
        const numero = Number(r[1].match(/\br="(\d+)"/)?.[1] ?? righe.length + 1);
        const riga = [];
        for (const c of (r[2] ?? '').matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
            const attr = c[1];
            const rif = attr.match(/\br="([A-Z]+)\d+"/)?.[1];
            const col = rif ? [...rif].reduce((n, l) => n * 26 + l.charCodeAt(0) - 64, 0) - 1 : riga.length;
            const tipo = attr.match(/\bt="([^"]+)"/)?.[1];
            const dentro = c[2] ?? '';
            const v = dentro.match(/<v>([\s\S]*?)<\/v>/)?.[1];
            let val = '';
            if (tipo === 's' && v !== undefined)
                val = condivise[Number(v)] ?? '';
            else if (tipo === 'inlineStr')
                val = iT(dentro);
            else if (v !== undefined) {
                val = testoXml(v);
                // Un telefono salvato come NUMERO può arrivare scritto «3.93331234567E11».
                if (/^\d(\.\d+)?E\+?\d+$/i.test(val))
                    val = String(Number(val));
            }
            riga[col] = val;
        }
        righe[numero - 1] = Array.from(riga, (x) => x ?? '');
    }
    return Array.from(righe, (x) => x ?? []);
}
// ── Riconoscere il file, e tirarne fuori le persone ──────────────────────────
const chiaveColonna = (s) => s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
/**
 * I nomi tutti maiuscoli o tutti minuscoli si rimettono in ordine: «ROSSI» e
 * «rossi» diventano «Rossi», «d'aprile» diventa «D'Aprile». 📏 Nell'export di
 * Matchpoint del 25/09/2026 sono 139 nomi tutti minuscoli e 12 tutti maiuscoli.
 * ⛔ Un nome scritto misto («McArthur», «De Luca») si lascia com'è.
 */
export function sistemaMaiuscole(s) {
    const t = s.trim().replace(/\s+/g, ' ');
    if (t !== t.toLowerCase() && t !== t.toUpperCase())
        return t;
    return t
        .toLowerCase()
        .replace(/(^|[\s'’-])(\p{L})/gu, (_m, prima, l) => prima + l.toUpperCase());
}
const emailPulita = (s) => {
    const e = (s ?? '').trim().toLowerCase();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e) ? e : null;
};
/** Da una casella di Google che può avere più numeri («… ::: …»), il primo che è un numero. */
function primoTelefono(valori) {
    let scritto = '';
    for (const v of valori) {
        for (const pezzo of v.split(':::')) {
            if (!pezzo.trim())
                continue;
            scritto ||= pezzo.trim();
            const t = normalizzaTelefono(pezzo);
            if (t)
                return { telefono: t, scritto: pezzo.trim() };
        }
    }
    return { telefono: null, scritto };
}
/**
 * Di chi è questo file, e le sue persone. ⛔ Un file che non è né l'uno né
 * l'altro si rifiuta: meglio un «non lo riconosco» che una colonna presa per
 * un'altra.
 */
export function contattiDalFoglio(righe) {
    const testa = (righe[0] ?? []).map(chiaveColonna);
    const pos = (...nomi) => {
        for (const n of nomi) {
            const i = testa.indexOf(n);
            if (i >= 0)
                return i;
        }
        return -1;
    };
    const cella = (r, i) => (i >= 0 ? (r[i] ?? '').trim() : '');
    const corpo = righe.slice(1).map((r, i) => ({ r, riga: i + 2 }));
    const vuota = (r) => r.every((x) => !x || !x.trim());
    // ── Matchpoint: «Nome», «Cognome», «Telefono cellulare», «E-mail», «Sesso» ──
    if (pos('telefonocellulare') >= 0 && pos('cognome') >= 0) {
        const [iN, iC, iT, iE, iS] = [
            pos('nome'),
            pos('cognome'),
            pos('telefonocellulare'),
            pos('email'),
            pos('sesso'),
        ];
        return {
            fonte: 'M',
            contatti: corpo
                .filter(({ r }) => !vuota(r))
                .map(({ r, riga }) => {
                const scritto = cella(r, iT);
                const sesso = cella(r, iS).toLowerCase();
                return {
                    fonte: 'M',
                    riga,
                    nome: sistemaMaiuscole(cella(r, iN)),
                    cognome: sistemaMaiuscole(cella(r, iC)),
                    telefono: normalizzaTelefono(scritto),
                    telefonoScritto: scritto,
                    email: emailPulita(cella(r, iE)),
                    sesso: sesso === 'uomo' ? 'M' : sesso === 'donna' ? 'F' : null,
                };
            }),
        };
    }
    // ── Google Contatti, «Google CSV» (vecchio e nuovo) ──
    const iNome = pos('firstname', 'givenname');
    const iCogn = pos('lastname', 'familyname');
    const telefoni = testa.flatMap((h, i) => (/^phone\d+value$/.test(h) ? [i] : []));
    const email = testa.flatMap((h, i) => (/^email\d+value$/.test(h) ? [i] : []));
    if ((iNome >= 0 || iCogn >= 0) && (telefoni.length > 0 || email.length > 0)) {
        const iIntero = pos('name', 'fileas');
        return {
            fonte: 'G',
            contatti: corpo
                .filter(({ r }) => !vuota(r))
                .map(({ r, riga }) => {
                let nome = cella(r, iNome);
                let cognome = cella(r, iCogn);
                if (!nome && !cognome) {
                    const intero = cella(r, iIntero).split(/\s+/).filter(Boolean);
                    nome = intero[0] ?? '';
                    cognome = intero.slice(1).join(' ');
                }
                const tel = primoTelefono(telefoni.map((i) => cella(r, i)));
                return {
                    fonte: 'G',
                    riga,
                    nome: sistemaMaiuscole(nome),
                    cognome: sistemaMaiuscole(cognome),
                    telefono: tel.telefono,
                    telefonoScritto: tel.scritto,
                    email: email.map((i) => emailPulita(cella(r, i))).find(Boolean) ?? null,
                    sesso: null,
                };
            }),
        };
    }
    throw new FileIllegibile('Non riconosco questo file. Servono l’esportazione dei clienti di Matchpoint (con «Telefono cellulare») o la rubrica Google esportata come «Google CSV».');
}
const stessoNome = (a, b) => chiaveColonna(a.nome + a.cognome) === chiaveColonna(b.nome + b.cognome);
/**
 * Le persone dei file, con i doppioni uniti, e cosa succederebbe a ciascuna.
 * Non scrive niente.
 *
 * ⚖️ **Quando due righe sono la stessa persona:**
 * · stesso telefono, o (se il telefono manca) stessa email;
 * · fra i DUE file basta quello: in rubrica la gente si chiama «Mario padel», e
 *   vince il nome di Matchpoint, che il circolo ha scritto per esteso;
 * · dentro lo STESSO file serve anche lo stesso nome. Stesso telefono e nome
 *   diverso (📏 1 caso nell'export del 25/09: una famiglia con un numero
 *   solo?) non si unisce e non si indovina: la seconda riga si scarta, e si
 *   aggiunge a mano. Il telefono nell'anagrafica è di una persona sola.
 */
export function righeDaImportare(contatti, soci) {
    // Matchpoint prima: a parità di persona, vince il nome scritto dal circolo.
    const ordinati = [...contatti].sort((a, b) => a.fonte === b.fonte ? a.riga - b.riga : a.fonte === 'M' ? -1 : 1);
    const persone = [];
    const perTel = new Map();
    const perEmail = new Map();
    const dove = (c) => `${c.fonte} ${c.riga}`;
    for (const c of ordinati) {
        const nuova = (scarto) => ({
            da: new Set([c.fonte]),
            righe: [dove(c)],
            nome: c.nome,
            cognome: c.cognome,
            telefono: c.telefono,
            email: c.email,
            sesso: c.sesso,
            note: [],
            scarto,
        });
        if (!c.nome || !c.cognome) {
            persone.push(nuova(!c.nome && !c.cognome ? 'senza nome' : !c.cognome ? 'manca il cognome' : 'manca il nome'));
            continue;
        }
        if (!c.telefono && !c.email) {
            persone.push(nuova(c.telefonoScritto
                ? `il telefono «${c.telefonoScritto}» non è un numero, e l’email manca: non si sa chi sia`
                : 'né telefono né email: non c’è modo di riconoscerlo'));
            continue;
        }
        const gia = (c.telefono ? perTel.get(c.telefono) : undefined) ?? (c.email ? perEmail.get(c.email) : undefined);
        if (gia) {
            const perTelefono = c.telefono !== null && gia.telefono === c.telefono;
            const stessoFile = gia.da.has(c.fonte);
            if (stessoFile && !stessoNome(gia, c)) {
                persone.push(nuova(`${perTelefono ? 'stesso telefono' : 'stessa email'} di ${gia.nome} ${gia.cognome} (${gia.righe[0]}): si aggiunge a mano`));
                continue;
            }
            if (!stessoFile) {
                gia.note.push(`unita: ${perTelefono ? 'stesso telefono' : 'stessa email'} nei due file` +
                    (stessoNome(gia, c) ? '' : ` (in rubrica «${c.nome} ${c.cognome}»)`));
            }
            else
                gia.note.push(`ripetuta nel file (${dove(c)})`);
            gia.da.add(c.fonte);
            gia.righe.push(dove(c));
            gia.telefono ??= c.telefono;
            gia.email ??= c.email;
            gia.sesso ??= c.sesso;
            if (c.telefono && !perTel.has(c.telefono))
                perTel.set(c.telefono, gia);
            if (c.email && !perEmail.has(c.email))
                perEmail.set(c.email, gia);
            continue;
        }
        const p = nuova(null);
        if (c.telefonoScritto && !c.telefono)
            p.note.push(`il telefono «${c.telefonoScritto}» non è un numero: entra senza`);
        persone.push(p);
        if (c.telefono)
            perTel.set(c.telefono, p);
        if (c.email)
            perEmail.set(c.email, p);
    }
    // ── contro l'anagrafica ──
    const sTel = new Map();
    const sEmail = new Map();
    for (const s of soci) {
        if (s.phone)
            sTel.set(s.phone, s);
        if (s.email)
            sEmail.set(s.email.toLowerCase(), s);
    }
    return persone.map((p) => {
        const base = {
            da: FONTI_IMPORT.filter((f) => p.da.has(f)),
            righe: p.righe.join(' · '),
            firstName: p.nome,
            lastName: p.cognome,
            phone: p.telefono,
            email: p.email,
            gender: p.sesso,
            aggiunge: {},
        };
        const chiave = p.telefono ?? (p.email ? `email:${p.email}` : `riga:${p.righe[0]}`);
        if (p.scarto)
            return { ...base, chiave: `riga:${p.righe[0]}`, esito: 'scartato', memberId: null, perché: p.scarto };
        const socio = (p.telefono ? sTel.get(p.telefono) : undefined) ?? (p.email ? sEmail.get(p.email) : undefined);
        if (!socio) {
            return { ...base, chiave, esito: 'nuovo', memberId: null, perché: p.note.join(' · ') || null };
        }
        // ⚖️ «Resta la scheda»: si riempiono solo le caselle vuote, e solo con
        // valori che nessun altro socio ha già.
        const aggiunge = {};
        const diverso = [];
        if (p.email && !socio.email && !sEmail.has(p.email))
            aggiunge.email = p.email;
        else if (p.email && socio.email && socio.email.toLowerCase() !== p.email)
            diverso.push('un’altra email');
        if (p.telefono && !socio.phone && !sTel.has(p.telefono))
            aggiunge.phone = p.telefono;
        else if (p.telefono && socio.phone && socio.phone !== p.telefono)
            diverso.push('un altro telefono');
        if (p.sesso && !socio.gender)
            aggiunge.gender = p.sesso;
        const chi = `c’è già (${socio.firstName} ${socio.lastName})`;
        const resta = diverso.length ? `: nella scheda ha ${diverso.join(' e ')}, e resta quella` : '';
        const campi = Object.keys(aggiunge).map((k) => ({ email: 'l’email', phone: 'il telefono', gender: 'il sesso' })[k]);
        if (campi.length === 0) {
            return {
                ...base,
                chiave,
                esito: 'uguale',
                memberId: socio.id,
                perché: `${chi}${resta || ', e non cambierebbe niente'}`,
            };
        }
        return {
            ...base,
            chiave,
            aggiunge,
            esito: 'aggiorna',
            memberId: socio.id,
            perché: `${chi}: si aggiunge ${campi.join(' e ')}, che nella scheda manca${campi.length > 1 ? 'no' : ''}${resta ? ` · ${resta.slice(2)}` : ''}`,
        };
    });
}
//# sourceMappingURL=import-soci.js.map